/*
 * /patient/$patientId (page)
 */
import { Card, CardContent, CardHeader, CardTitle, Container, Page, Placeholder } from "@emr/react-ui"
import { createFileRoute, Link, useLocation } from "@tanstack/react-router"
import { cn } from "@emr/react-ui"
import { trpc } from "../../trpc"
import { GeneralNoteList } from "../../features/generalnote/general-note-list"
import type { Patient } from "@emr/db/client"

export const Route = createFileRoute("/patient/$patientId")({
	component: PatientPage,
})

/* ─── Mock patient (used when DB returns nothing) ────────────────────────── */

const MOCK_PATIENT: Partial<Patient> = {
	firstName: "John",
	lastName: "Doe",
	dateOfBirth: new Date("1978-08-19") as any,
	bloodType: "O+",
	allergies: "Penicillin",
	emergencyContactName: "Jane Doe (Spouse)",
	emergencyContactPhone: "(506) 897-6116",
}

const NOW_MS = Date.now()

/* ─── Nav tabs ───────────────────────────────────────────────────────────── */

const NAV_TABS = [
	{ label: "Notes", to: "/patient/$patientId" as const },
	{ label: "Treatment Plan", to: "/treatment/$patientId" as const },
	{ label: "Prescription", to: "/prescription/$patientId" as const },
	{ label: "Intake Form", to: "/intake/$patientId" as const },
]

/* ─── Page ───────────────────────────────────────────────────────────────── */

function PatientPage() {
	const { patientId } = Route.useParams()

	const { data: patient, isLoading: patientLoading } =
		trpc.patient.getByFilter.useQuery({ where: { id: patientId } })

	const { data: notesData, isLoading: notesLoading } =
		trpc.generalNote.getAllByFilter.useQuery({
			where: { patientId, active: true },
		})
	const notes = notesData ?? []

	const displayPatient = patient ?? (MOCK_PATIENT as Patient)
	const isLoading = patientLoading || notesLoading

	return (
		<Page centerVertical={false}>
			<PatientHeader patient={displayPatient} patientId={patientId} />
			<Container width="xl" padding="lg" gap="lg">
				{isLoading ? (
					<Placeholder title="Loading..." />
				) : (
					<Card>
						<CardHeader>
							<CardTitle>General Notes</CardTitle>
						</CardHeader>
						<CardContent>
							<GeneralNoteList patientId={patientId} notes={notes} />
						</CardContent>
					</Card>
				)}
			</Container>
		</Page>
	)
}

/* ─── Patient header ─────────────────────────────────────────────────────── */

function PatientHeader({ patient, patientId }: { patient: Patient; patientId: string }) {
	const location = useLocation()
	const currentPath = location.pathname

	const initials =
		(patient.firstName?.[0] ?? "") + (patient.lastName?.[0] ?? "")

	const dob = patient.dateOfBirth ? new Date(patient.dateOfBirth) : null
	const age = dob
		? Math.floor((NOW_MS - dob.getTime()) / (365.25 * 24 * 60 * 60 * 1000))
		: null
	const dobFormatted = dob
		? dob.toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })
		: null

	return (
		<div className="w-full border-b bg-card shadow-sm">
			{/* Info band */}
			<div className="max-w-[1000px] mx-auto px-8 py-5 flex flex-col sm:flex-row items-start sm:items-center gap-5">
				{/* Avatar */}
				<div className="flex-shrink-0 w-14 h-14 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xl font-bold select-none">
					{initials || "—"}
				</div>

				{/* Name + status */}
				<div className="flex-1 min-w-0">
					<h1 className="text-2xl font-semibold tracking-tight">
						{patient.firstName} {patient.lastName}
					</h1>
					<div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-muted-foreground">
						{dobFormatted && (
							<span>
								DOB:{" "}
								<span className="text-foreground font-medium">{dobFormatted}</span>
								{age !== null && (
									<span className="ml-1 text-muted-foreground">({age} yrs)</span>
								)}
							</span>
						)}
						{patient.bloodType && (
							<span>
								Blood:{" "}
								<span className="text-foreground font-medium">{patient.bloodType}</span>
							</span>
						)}
					</div>
				</div>

				{/* Vitals grid */}
				<div className="flex flex-wrap sm:flex-nowrap gap-4 text-sm shrink-0">
					{patient.allergies && (
						<Vital
							label="Allergies"
							value={patient.allergies}
							highlight={patient.allergies.toLowerCase() !== "none"}
						/>
					)}
					{patient.emergencyContactName && (
						<Vital label="Emergency" value={patient.emergencyContactName} />
					)}
					{patient.emergencyContactPhone && (
						<Vital label="Phone" value={patient.emergencyContactPhone} />
					)}
				</div>
			</div>

			{/* Nav tabs */}
			<div className="max-w-[1000px] mx-auto px-8">
				<nav className="flex gap-0">
					{NAV_TABS.map((tab) => {
						const href = tab.to.replace("$patientId", patientId)
						const isActive = currentPath === href

						return (
							<Link
								key={tab.to}
								to={tab.to}
								params={{ patientId }}
								className={cn(
									"relative px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap",
									"hover:text-foreground",
									isActive
										? "text-primary after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-primary after:rounded-t-full"
										: "text-muted-foreground",
								)}
							>
								{tab.label}
							</Link>
						)
					})}
				</nav>
			</div>
		</div>
	)
}

function Vital({
	label,
	value,
	highlight,
}: {
	label: string
	value: string
	highlight?: boolean
}) {
	return (
		<div className="flex flex-col gap-0.5">
			<span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
			<span className={cn("font-medium", highlight && "text-destructive")}>{value}</span>
		</div>
	)
}
